import { Component, ViewChild, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';

import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';

import { animate, state, style, transition, trigger } from '@angular/animations';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AircraftService } from 'src/app/service/aircraft.service';
import { Aircraft, Flight, LandingItem } from 'src/app/models';
import { CatalogPipe, DOMAIN } from 'src/app/catalog';
import { FlightService } from 'src/app/service/flight.service';
import { diffMinutes, minutesToHhMm } from 'src/app/utils/time.utils';

import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { catchError, debounceTime, distinctUntilChanged, map, merge, of, startWith, Subject, switchMap, tap } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { AppScreen } from 'src/app/shared/responsive.breakpoints';
import { ResponsiveService } from 'src/app/shared/responsive.service';
import { BreakpointObserver } from '@angular/cdk/layout';

@Component({
  selector: 'app-flight-list',
  standalone: true,
  imports: [
    CommonModule,
    // Form
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    // Table
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    // UI
    MatIconModule,
    MatButtonModule,
    RouterLink,
    CatalogPipe,
  ],
  templateUrl: './flight-list.component.html',
  styleUrls: ['./flight-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('chev', [
      state('collapsed', style({ transform: 'rotate(0deg)' })),
      state('expanded', style({ transform: 'rotate(90deg)' })),
      transition('expanded <=> collapsed', animate('120ms ease')),
    ]),
    trigger('detailRow', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, visibility: 'hidden' }),
        animate('150ms ease-out', style({ height: '*', opacity: 1, visibility: 'visible' })),
      ]),
      transition(':leave', [animate('120ms ease-in', style({ height: '0px', opacity: 0, visibility: 'hidden' }))]),
    ]),
  ],
})
export class FlightListComponent implements AfterViewInit {
  @ViewChild(MatTable) table!: MatTable<Flight>;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  readonly DOMAIN = DOMAIN;

  private readonly responsive = inject(ResponsiveService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly cdr = inject(ChangeDetectorRef);

  dataSource = new MatTableDataSource<Flight>([]);
  displayedColumns: string[] = ['code', 'date', 'crew', 'takeoff', 'landing', 'ldg', 'counterBegin', 'counterEnd', 'airtime', 'blocktime', 'pricing'];
  columnsConfig = {
    wide: ['code', 'date', 'crew', 'takeoff', 'landing', 'ldg', 'counterBegin', 'counterEnd', 'airtime', 'blocktime', 'pricing'],
    tabletLarge: ['code', 'date', 'crew', 'takeoff', 'landing', 'ldg', 'airtime', 'blocktime'],
    mobileLarge: ['code', 'date', 'takeoff', 'airtime', 'blocktime'],
    mobile: ['code', 'date', 'takeoff', 'airtime', 'blocktime'],
    mobileSmall: ['code', 'date', 'takeoff', 'airtime'],
  };

  // Filtres
  filters: FormGroup;
  aircraftOptions: Array<{ code: string; name?: string }> = [];

  // Pagination/chargement
  total = 0;
  pageSize = 10;
  pageIndex = 0;
  loading = false;

  // Déclencheur manuel (bouton Rechercher / init)
  private refresh$ = new Subject<void>();

  // Id de la ligne déployée
  expandedId: string | null = null;

  constructor(private fb: FormBuilder, private flightService: FlightService, private aircraftService: AircraftService, private router: Router) {
    // Form des filtres
    this.filters = this.fb.group({
      startDate: [null],
      endDate: [null],
      aircraftCodes: [[]],
    });

    // Options avions (si endpoint dispo)
    this.aircraftService
      .getAircrafts()
      .pipe(takeUntilDestroyed())
      .subscribe((list) => {
        this.aircraftOptions = list.map((a) => ({ code: a.code }));
        this.cdr.markForCheck();
      });
  }

  ngAfterViewInit(): void {
    // Tri initial (uniquement pour l’UI ; tri réel côté backend)
    this.dataSource.sort = this.sort;
    const initialSort: Sort = { active: 'date', direction: 'desc' };
    this.sort.active = initialSort.active;
    this.sort.direction = initialSort.direction;

    // Changements de filtres
    const filterChanges$ = this.filters.valueChanges.pipe(
      debounceTime(300),
      map((v) =>
        JSON.stringify({
          startDate: v.startDate ? this.normalizeDate(v.startDate) : null,
          endDate: v.endDate ? this.normalizeDate(v.endDate) : null,
          aircraftCodes: v.aircraftCodes ?? [],
        })
      ),
      distinctUntilChanged(),
      tap(() => this.paginator.firstPage())
    );

    // Tri -> revenir page 0
    const sortChanges$ = this.sort.sortChange.pipe(tap(() => this.paginator.firstPage()));

    // Pagination
    const pageChanges$ = this.paginator.page;

    merge(
      this.refresh$.pipe(startWith(void 0)), // premier chargement
      filterChanges$,
      sortChanges$,
      pageChanges$
    )
      .pipe(
        tap(() => {
          this.loading = true;
          this.cdr.markForCheck();
        }),
        switchMap(() => this.flightService.searchFlights(this.buildQuery()).pipe(catchError(() => of({ items: [], total: 0 })))),
        tap(() => {
          this.loading = false;
        })
      )
      .subscribe(({ items, total }) => {
        this.dataSource.data = items ?? [];
        this.total = total ?? 0;
        this.cdr.markForCheck();
      });

    this.observeBreakpoints();
  }

  private observeBreakpoints(): void {
    this.responsive.screen$.subscribe((screen) => {
      let cols: string[];

      switch (screen) {
        case AppScreen.MobileSmall:
          cols = this.columnsConfig.mobileSmall;
          break;
        case AppScreen.Mobile:
          cols = this.columnsConfig.mobile;
          break;
        case AppScreen.MobileLarge:
        case AppScreen.TabletSmall:
          cols = this.columnsConfig.mobile;
          break;
        case AppScreen.TabletLarge:
          cols = this.columnsConfig.tabletLarge;
          break;
        case AppScreen.Desktop:
        case AppScreen.DesktopLarge:
        case AppScreen.Wide:
        default:
          cols = this.columnsConfig.wide;
          break;
      }

      // ajoute toujours la colonne 'expand' en tête
      this.displayedColumns = ['expand', ...cols];

      // OnPush + MatTable : applique + rerender
      this.cdr.detectChanges();
      this.table?.renderRows();
      (this.table as any)?.updateStickyColumnStyles?.(); // si header sticky
    });
  }

  /** ---- Actions filtres ---- */
  applyFilters(): void {
    this.paginator.firstPage();
    this.refresh$.next();
  }

  setToday(): void {
    const today = new Date();
    this.filters.patchValue({ startDate: today, endDate: today });
    this.paginator.firstPage();
    this.refresh$.next();
  }

  resetFilters(): void {
    this.filters.reset({ startDate: null, endDate: null, aircraftCodes: [] });
    this.paginator.firstPage();
    this.refresh$.next();
  }

  /** ---- Helpers ---- */
  private normalizeDate(d: string | Date): string {
    const date = new Date(d);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private buildQuery() {
    const f = this.filters.value as {
      startDate: Date | null;
      endDate: Date | null;
      aircraftCodes: string[];
    };
    return {
      startDate: f.startDate ? this.normalizeDate(f.startDate) : null,
      endDate: f.endDate ? this.normalizeDate(f.endDate) : null,
      aircraftCodes: f.aircraftCodes ?? [],
      page: this.paginator.pageIndex,
      size: this.paginator.pageSize,
      sort: this.sort.active || 'date',
      dir: this.sort.direction || 'desc',
    };
  }

  /** ---- Table utils ---- */
  computeLandingsNumber(landingsList: LandingItem[]): number {
    let count = 0;
    landingsList?.forEach((el) => (count += el.count));
    return count;
  }

  formatMinutesToHours(mins: number): string {
    return minutesToHhMm(mins);
  }

  formatHoursToMinutes(hours: number): string {
    return hours + ':00';
  }

  diffMinutes(date1: Date, date2: Date): number {
    return diffMinutes(date1, date2);
  }

  // Détail expand/collapse
  @ViewChild(MatTable) private _table!: MatTable<Flight>;
  isDetailRow = (_: number, row: Flight) => row.id === this.expandedId;

  toggleRow(row: Flight): void {
    if (!row?.id) return;
    this.expandedId = this.expandedId === row.id ? null : row.id;
    this.cdr.detectChanges();
    this._table.renderRows();
  }

  isExpanded(row: Flight): boolean {
    return this.expandedId === row.id;
  }

  trackById = (_: number, row: Flight) => row.id;

  goto(): void {
    this.router.navigate(['/flights']);
  }
}
